/**
 * ProtectedRoute - Protège les routes selon le rôle de l'utilisateur
 *
 * Usage:
 * <Route path="/dashboard" element={<ProtectedRoute roles={['ADMIN', 'MANAGER']}><Dashboard /></ProtectedRoute>} />
 *
 * Règles d'accès:
 * - ADMIN: Accès total
 * - MANAGER: Accès partiel (Catalogue, POS, Commandes, Clients)
 * - CASHIER: POS uniquement (pas de login requis, route publique)
 */
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: UserRole[];
  requireAuth?: boolean; // Par défaut: true
}

export default function ProtectedRoute({
  children,
  roles,
  requireAuth = true,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Afficher un loader pendant la vérification de l'authentification
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Si authentification requise mais utilisateur non connecté → Redirect vers /login
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si rôles spécifiés, vérifier que l'utilisateur a l'un des rôles requis
  if (roles && roles.length > 0 && user) {
    const hasRequiredRole = roles.includes(user.role);

    if (!hasRequiredRole) {
      // Utilisateur connecté mais n'a pas le bon rôle
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès Refusé</h1>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Votre rôle :</strong> {user.role}
                <br />
                <strong>Rôles requis :</strong> {roles.join(', ')}
              </p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      );
    }
  }

  // Tout est OK, afficher le contenu
  return <>{children}</>;
}
